import { useCallback, useEffect, useState } from 'react';

/**
 * Wraps a callback in a check to see if a DOM element is visible on the page.  When the dom
 * element becomes visible for the first time, fire the callback
 */
export const useInViewportCallback = ({
  callback,
  ref,
}: {
  callback: () => void;
  ref: React.MutableRefObject<any>;
}): void => {
  const [hasBeenFired, setHasBeenFired] = useState(false);
  const isInViewport = useCallback(() => {
    if (ref.current) {
      const element = ref.current;
      const rect = element.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
    }

    return false;
  }, [ref]);

  const handleScroll = useCallback(() => {
    if (isInViewport()) {
      callback();
      setHasBeenFired(true);
    }
  }, [callback, isInViewport]);

  const [onReadyFired, setOnReadyFired] = useState(false);
  useEffect(() => {
    if (ref.current && !onReadyFired) {
      handleScroll();
      setOnReadyFired(true);
    }
  }, [handleScroll, onReadyFired, ref]);

  useEffect(() => {
    if (hasBeenFired) {
      window.removeEventListener('scroll', handleScroll);
    } else {
      window.addEventListener('scroll', handleScroll);
    }
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, hasBeenFired]);
};

export default useInViewportCallback;
